import React, { useEffect, useRef, useState } from "react";
import { Play, Square, Music } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { playFreq } from "@/lib/audio";
import { pitchToFreq } from "@/lib/music";
import { freqToMidi, midiName } from "@/lib/mic";

// [pitch in C, beats, syllable]; "|" = bar line, "/" = new stave line.
const SONGS = [
  { id: "twinkle", group: "Traditional", title: "Twinkle, Twinkle, Little Star", time: "4/4",
    notes: "C4 1 Twin-|C4 1 kle,|G4 1 twin-|G4 1 kle,|A4 1 lit-|A4 1 tle|G4 2 star,|/|F4 1 how|F4 1 I|E4 1 won-|E4 1 der|D4 1 what|D4 1 you|C4 2 are." },
  { id: "jacques", group: "Traditional", title: "Are You Sleeping? (Frère Jacques)", time: "4/4",
    notes: "C4 1 Are|D4 1 you|E4 1 sleep-|C4 1 ing,|C4 1 are|D4 1 you|E4 1 sleep-|C4 1 ing,|/|E4 1 Bro-|F4 1 ther|G4 2 John,|E4 1 Bro-|F4 1 ther|G4 2 John?" },
  { id: "joy", group: "Traditional", title: "Ode to Joy", time: "4/4",
    notes: "E4 1 Joy-|E4 1 ful,|F4 1 joy-|G4 1 ful,|G4 1 we|F4 1 a-|E4 1 dore|D4 1 thee,|/|C4 1 God|C4 1 of|D4 1 glo-|E4 1 ry,|E4 1.5 Lord|D4 0.5 of|D4 2 love." },
  { id: "jingle", group: "Christmas", title: "Jingle Bells", time: "4/4",
    notes: "E4 1 Jin-|E4 1 gle|E4 2 bells,|E4 1 jin-|E4 1 gle|E4 2 bells,|/|E4 1 jin-|G4 1 gle|C4 1.5 all|D4 0.5 the|E4 4 way!" },
  { id: "wish", group: "Christmas", title: "We Wish You a Merry Christmas", time: "3/4", pickup: 1,
    notes: "G3 1 We|C4 1 wish|C4 0.5 you|D4 0.5 a|C4 0.5 mer-|B3 0.5 ry|A3 1 Christ-|A3 1 mas,|A3 1 we|/|D4 1 wish|D4 0.5 you|E4 0.5 a|D4 0.5 mer-|C4 0.5 ry|B3 1 Christ-|G3 1 mas,|G3 1 we|/|E4 1 wish|E4 0.5 you|F4 0.5 a|E4 0.5 mer-|D4 0.5 ry|C4 1 Christ-|A3 1 mas,|G3 0.5 and|G3 0.5 a|/|A3 1 hap-|D4 1 py|B3 1 New|C4 3 Year!" },
  { id: "deck", group: "Christmas", title: "Deck the Halls", time: "4/4",
    notes: "G4 1.5 Deck|F4 0.5 the|E4 1 halls|D4 1 with|C4 1 boughs|D4 1 of|E4 1 hol-|C4 1 ly,|/|D4 0.5 fa|E4 0.5 la|F4 0.5 la|D4 0.5 la|E4 1.5 la,|D4 0.5 la|C4 1 la|B3 1 la|C4 2 la!" },
];
const BAR = { "3/4": 3, "4/4": 4 };
// Semitone shift and clef so each part sings the tune in a comfortable key.
export const PART_KEYS = { Soprano: { shift: 7, clef: "treble", key: "G major" }, Alto: { shift: 0, clef: "treble", key: "C major" }, Tenor: { shift: -5, clef: "bass", key: "G major" }, Bass: { shift: -12, clef: "bass", key: "C major" } };
const DUR = { 0.5: ["eighth"], 1: ["quarter"], 1.5: ["quarter", true], 2: ["half"], 3: ["half", true], 4: ["whole"] };
const CHORDS = [[0, 4, 7], [7, 11, 2], [5, 9, 0]];

const parse = (song, shift) => {
  const lines = [[]];
  let beat = 0;
  song.notes.split("|").forEach((tok) => {
    if (tok === "/") {
      const end = beat - (song.pickup || 0);
      if (end > 0 && end % BAR[song.time] === 0) lines[lines.length - 1].push({ barline: true });
      lines.push([]); return;
    }
    const [p, b, word] = tok.split(" ");
    const pitch = midiName(freqToMidi(pitchToFreq(p)) + shift);
    const [duration, dot] = DUR[b];
    const line = lines[lines.length - 1];
    const pos = beat - (song.pickup || 0);
    if (pos >= 0 && pos % BAR[song.time] === 0 && line.length) line.push({ barline: true });
    line.push({ pitch, label: word, duration, dot, beats: Number(b) });
    beat += Number(b);
  });
  return lines;
};

export const VoicePartSongs = ({ part: initial = "Alto", testId = "part-songs" }) => {
  const [part, setPart] = useState(initial);
  const [song, setSong] = useState(SONGS[0]);
  const [active, setActive] = useState(-1);
  const [withTune, setWithTune] = useState(true);
  const [withChords, setWithChords] = useState(true);
  const timers = useRef([]);
  useEffect(() => setPart(initial), [initial]);
  const stop = () => { timers.current.forEach(clearTimeout); timers.current = []; setActive(-1); };
  useEffect(() => stop, []);
  const lines = parse(song, PART_KEYS[part].shift);
  const flat = lines.flat().filter((n) => !n.barline);
  const play = () => {
    stop();
    const spb = 0.55, tonic = ((PART_KEYS[part].shift % 12) + 12) % 12, seg = BAR[song.time] === 3 ? 3 : 2;
    let t = 0;
    const segs = {};
    flat.forEach((n, k) => {
      if (withTune) timers.current.push(setTimeout(() => { setActive(k); playFreq(pitchToFreq(n.pitch), n.beats * 0.5); }, t * 1000));
      else timers.current.push(setTimeout(() => setActive(k), t * 1000));
      const s = Math.floor((t / spb + 1e-6 - (song.pickup || 0)) / seg);
      (segs[s] = segs[s] || []).push([freqToMidi(pitchToFreq(n.pitch)) % 12, n.beats]);
      t += n.beats * spb;
    });
    if (withChords) {
      Object.entries(segs).forEach(([s, ns]) => {
        const best = CHORDS.map((c) => ({ c, v: ns.reduce((a, [pc, b]) => a + (c.includes((pc - tonic + 12) % 12) ? b : 0), 0) })).sort((a, b) => b.v - a.v)[0].c;
        const start = Math.max(0, (Number(s) * seg + (song.pickup || 0)) * spb);
        timers.current.push(setTimeout(() => best.forEach((iv) => playFreq(pitchToFreq(midiName(48 + ((tonic + iv) % 12))), seg * spb, 0.22)), start * 1000));
      });
    }
    timers.current.push(setTimeout(() => setActive(-1), t * 1000));
  };
  let offset = 0;
  return (
    <div className="mt-5 rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid={testId}>
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Music size={14} /> Songs for your voice part</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testId}-parts`}>
        {Object.keys(PART_KEYS).map((p) => (
          <button key={p} onClick={() => { stop(); setPart(p); }} data-testid={`${testId}-part-${p.toLowerCase()}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${p === part ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{p}</button>
        ))}
      </div>
      {["Traditional", "Christmas"].map((g) => (
        <div key={g} className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{g === "Christmas" ? "Christmas songs" : "Traditional tunes"}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {SONGS.filter((s) => s.group === g).map((s) => (
              <button key={s.id} onClick={() => { stop(); setSong(s); }} data-testid={`${testId}-song-${s.id}`}
                className={`rounded-xl px-3 py-2 text-sm transition-colors ${s.id === song.id ? "bg-primary/15 text-primary ring-1 ring-primary" : "bg-muted hover:bg-muted/70"}`}>{s.title}</button>
            ))}
          </div>
        </div>
      ))}
      <p className="mt-4 text-sm text-muted-foreground" data-testid={`${testId}-info`}><b className="text-foreground">{song.title}</b> for {part}: in {PART_KEYS[part].key}, {PART_KEYS[part].clef} clef. Listen first, then sing along with the words underneath.</p>
      <div className="mt-3 space-y-2">
        {lines.map((ln, k) => {
          const start = offset;
          offset += ln.filter((n) => !n.barline).length;
          let c = -1;
          return <StaffViewer key={`${song.id}-${part}-${k}`} clef={PART_KEYS[part].clef} time={k === 0 ? song.time : undefined} meter={song.time} width={Math.max(360, ln.length * 50 + 110)} showLabels playOnClick
            notes={ln.map((n) => (n.barline ? n : { ...n, color: start + (c += 1) === active ? "#F59E0B" : undefined }))} testId={`${testId}-stave-${k}`} />;
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button onClick={play} data-testid={`${testId}-play`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:scale-105"><Play size={13} /> Play</button>
        <button onClick={stop} data-testid={`${testId}-stop`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs"><Square size={12} /> Stop</button>
        <label className="flex items-center gap-1.5 text-xs" data-testid={`${testId}-tune-toggle`}><input type="checkbox" checked={withTune} onChange={(e) => setWithTune(e.target.checked)} /> Play the tune</label>
        <label className="flex items-center gap-1.5 text-xs" data-testid={`${testId}-chords-toggle`}><input type="checkbox" checked={withChords} onChange={(e) => setWithChords(e.target.checked)} /> Piano chords</label>
      </div>
    </div>
  );
};
